// LO QUE SE ESTABA ESCRIBIENDO, RESCATADO ANTES DE CERRAR.
//
// EL PROBLEMA. Un cierre por inactividad que pierda lo tecleado enseña a
// la gente a dejar la sesión abierta a propósito — y entonces la medida
// de seguridad produce exactamente lo que venía a evitar. El dueño lo
// puso como condición: *«cerrar no debe costarle trabajo al usuario»*.
//
// CÓMO, Y POR QUÉ ASÍ. No se toca ni un formulario. Cuando la sesión está
// a punto de cerrarse se recorre el documento, se leen los campos que
// tienen algo escrito y se guardan con la dirección en la que estaban. Al
// volver a entrar a esa misma dirección se devuelven.
//
// La alternativa —que cada formulario guarde el suyo— habría significado
// tocar cuarenta pantallas, y bastaría olvidarse de una para que quien la
// usara perdiera su trabajo sin saber por qué. Esto acierta en todas o en
// ninguna.
//
// LO QUE NO RESCATA, dicho para que nadie cuente con ello: un archivo a
// medio subir, un dibujo en un lienzo, y nada de lo que viva solo en la
// memoria de un componente sin llegar a un campo. Y tampoco escribe nada
// si la pantalla cambió de forma. **Es un rescate, no un respaldo.**
//
// DÓNDE SE GUARDA. En `sessionStorage`, que muere con la pestaña. Ni en
// `localStorage` —lo escrito por alguien no puede sobrevivir a que otro
// se siente en el mismo aparato— ni en el servidor: un borrador a medias
// no es un dato de la empresa todavía.

const CLAVE = 'tego_borrador';

// LAS CONTRASEÑAS Y LOS PIN NO SE RESCATAN NUNCA. Guardar una contraseña
// «para no perderla» es exactamente la clase de comodidad que convierte
// una medida de seguridad en un agujero.
//
// El PIN de autorización del punto de venta va como `type="password"`, así
// que ya cae aquí; se mira además la etiqueta porque el día que alguien
// escriba un campo de secreto sin ese tipo, esto lo sigue atrapando.
const PROHIBIDOS = ['password', 'hidden', 'file', 'submit', 'button', 'image'];
const SUENA_A_SECRETO = /(contrase|password|\bpin\b|clave|token|cvv|tarjeta)/i;

function etiquetaDe(el) {
  const propia = el.getAttribute?.('aria-label') || el.placeholder || '';
  if (propia) return propia.trim().slice(0, 60);
  // El `<label for="...">`, o el `<label>` que lo envuelve.
  const id = el.id;
  const doc = el.ownerDocument || document;
  const marca = (id && doc.querySelector(`label[for="${CSS.escape(id)}"]`))
    || el.closest?.('label');
  return (marca?.textContent || '').trim().slice(0, 60);
}

function seRescata(el) {
  if (PROHIBIDOS.includes(el.type)) return false;
  if (el.disabled || el.readOnly) return false;
  if (el.autocomplete === 'current-password'
      || el.autocomplete === 'new-password'
      || el.autocomplete === 'one-time-code') return false;
  // POR SI ACASO, Y POR EL NOMBRE. El tipo lo pone quien escribe la
  // pantalla y puede olvidarlo; el rótulo casi nunca miente.
  if (SUENA_A_SECRETO.test(
    `${el.name || ''} ${el.id || ''} ${etiquetaDe(el)}`)) return false;
  return true;
}

// CÓMO SE IDENTIFICA UN CAMPO, Y POR QUÉ NO POR SU NOMBRE.
//
// La primera versión los guardaba por `name` o `id`. Se midió antes de
// darla por buena: de los 198 campos de TEGOCLOUD, **14** tienen alguno de
// los dos. Con esa llave el rescate no rescataba nada — y una red que no
// atrapa nada es peor que ninguna, porque se confía en ella.
//
// Así que la llave es POSICIONAL: en qué puesto está el campo dentro de la
// pantalla. Es estable mientras se vuelva a la MISMA pantalla, que es el
// único caso en el que se devuelve nada.
//
// Y COMO UNA LLAVE POSICIONAL SE PUEDE EQUIVOCAR —basta con que un campo
// condicional aparezca o desaparezca— se guarda además su forma: el tipo y
// su etiqueta. Al devolverlo se comprueban las dos, y si no cuadran NO SE
// ESCRIBE. Un campo que no se rescata es una molestia; el nombre de un
// proveedor metido en la casilla de la cantidad es un dato falso que nadie
// va a notar.
const formaDe = (el) => `${el.type || el.tagName}|${etiquetaDe(el)}`;

function campos(doc) {
  return [...doc.querySelectorAll('input, textarea, select')];
}

/** La dirección actual, que es la llave: lo escrito vuelve a SU pantalla. */
function donde() {
  return window.location.pathname + window.location.search;
}

/**
 * Recoge lo que haya escrito ahora mismo. Devuelve cuántos campos guardó.
 *
 * Se llama justo antes de cerrar la sesión, no todo el rato: escribir en
 * `sessionStorage` en cada tecla es trabajo constante para un caso que
 * ocurre una vez cada varias horas.
 */
export function guardarBorrador(doc = document) {
  try {
    const guardados = [];
    campos(doc).forEach((el, indice) => {
      if (!seRescata(el)) return;
      const valor = el.type === 'checkbox' || el.type === 'radio'
        ? el.checked : el.value;
      // Lo vacío no se guarda: rellenaría el rescate de ruido y, al
      // devolverlo, borraría valores por omisión que sí estaban bien.
      if (valor === '' || valor === false || valor === null
          || valor === undefined) return;
      guardados.push({ indice, forma: formaDe(el), valor });
    });

    if (!guardados.length) {
      window.sessionStorage.removeItem(CLAVE);
      return 0;
    }
    window.sessionStorage.setItem(CLAVE, JSON.stringify({
      donde: donde(), cuando: Date.now(), campos: guardados,
    }));
    return guardados.length;
  } catch {
    // Modo privado, cuota llena: se pierde el rescate, no la sesión.
    return 0;
  }
}

/** ¿Hay algo guardado para esta dirección? Devuelve cuántos campos. */
export function hayBorrador() {
  try {
    const crudo = window.sessionStorage.getItem(CLAVE);
    if (!crudo) return 0;
    const b = JSON.parse(crudo);
    return b.donde === donde() ? (b.campos || []).length : 0;
  } catch {
    return 0;
  }
}

/**
 * Devuelve lo escrito a sus campos. Devuelve cuántos pudo colocar.
 *
 * EL DETALLE QUE LO HACE FUNCIONAR CON REACT. Escribirle `.value` a un
 * campo controlado no le dice nada a React: el estado sigue vacío y el
 * primer render lo borra. Hay que usar el `setter` nativo del prototipo y
 * después disparar el evento, que es lo que React escucha de verdad. Sin
 * esto el rescate se ve un instante y desaparece, que es peor que no
 * rescatar nada.
 */
export function devolverBorrador(doc = document) {
  let crudo;
  try {
    crudo = window.sessionStorage.getItem(CLAVE);
  } catch { return 0; }
  if (!crudo) return 0;

  let b;
  try { b = JSON.parse(crudo); } catch { return 0; }
  if (!b || b.donde !== donde()) return 0;

  const presentes = campos(doc);
  let puestos = 0;

  for (const guardado of b.campos || []) {
    const el = presentes[guardado.indice];
    // LAS DOS COMPROBACIONES QUE EVITAN UN DATO FALSO: que haya un campo
    // en ese puesto, y que sea EL MISMO campo. Si la pantalla cambió, no
    // se escribe nada.
    if (!el || !seRescata(el)) continue;
    if (formaDe(el) !== guardado.forma) continue;

    if (el.type === 'checkbox' || el.type === 'radio') {
      el.checked = !!guardado.valor;
      el.dispatchEvent(new Event('change', { bubbles: true }));
    } else {
      const proto = el instanceof window.HTMLTextAreaElement
        ? window.HTMLTextAreaElement.prototype
        : (el instanceof window.HTMLSelectElement
          ? window.HTMLSelectElement.prototype
          : window.HTMLInputElement.prototype);
      const ponerValor = Object.getOwnPropertyDescriptor(proto, 'value')?.set;
      if (ponerValor) ponerValor.call(el, guardado.valor);
      else el.value = guardado.valor;
      el.dispatchEvent(new Event('input', { bubbles: true }));
      el.dispatchEvent(new Event('change', { bubbles: true }));
    }
    puestos += 1;
  }
  olvidarBorrador();
  return puestos;
}

/** Se llama al devolverlo y al cerrar sesión a mano. */
export function olvidarBorrador() {
  try { window.sessionStorage.removeItem(CLAVE); } catch { /* da igual */ }
}
